import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ShiftForm from './components/ShiftForm';
import PreviewPanel from './components/PreviewPanel';
import TemplateLibrary from './components/TemplateLibrary';
import BulkCreationModal from './components/BulkCreationModal';

const ShiftCreationAndEditing = () => {
  const [selectedShift, setSelectedShift] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [recentShifts, setRecentShifts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Mock recent shifts data
  const mockRecentShifts = [
    {
      id: 1,
      title: 'Morgenschicht Kundenservice',
      department: 'customer-service',
      date: '2025-10-04',
      startTime: '08:00',
      endTime: '16:00',
      status: 'active',
      assignedEmployees: 2,
      maxEmployees: 3,
      createdAt: '2025-10-03T10:30:00'
    },
    {
      id: 2,
      title: 'Nachtschicht Sicherheit',
      department: 'security',
      date: '2025-10-04',
      startTime: '22:00',
      endTime: '06:00',
      status: 'draft',
      assignedEmployees: 0,
      maxEmployees: 2,
      createdAt: '2025-10-03T09:15:00'
    },
    {
      id: 3,
      title: 'Produktionsschicht',
      department: 'manufacturing',
      date: '2025-10-05',
      startTime: '06:00',
      endTime: '14:00',
      status: 'active',
      assignedEmployees: 6,
      maxEmployees: 8,
      createdAt: '2025-10-02T16:45:00'
    }
  ];

  useEffect(() => {
    setRecentShifts(mockRecentShifts);
  }, []);

  const handleSaveShift = (shiftData) => {
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      console.log('Saving shift:', shiftData);
      
      // Add to recent shifts if new
      if (!selectedShift) {
        const newShift = {
          id: Date.now(),
          ...shiftData,
          status: 'draft',
          assignedEmployees: 0,
          createdAt: new Date()?.toISOString()
        };
        setRecentShifts(prev => [newShift, ...prev?.slice(0, 4)]);
      }
      
      // Reset form
      setSelectedShift(null);
      setPreviewData(null);
      setIsLoading(false);
      
      // Show success message (in real app, use toast/notification)
      alert('Schicht erfolgreich gespeichert!');
    }, 1000);
  };

  const handleCancelEdit = () => {
    setSelectedShift(null);
    setPreviewData(null);
  };

  const handlePreviewUpdate = (data) => {
    setPreviewData(data);
  };

  const handleSelectTemplate = (templateData) => {
    setSelectedShift(templateData);
    setPreviewData(templateData);
    setShowTemplateLibrary(false);
  };

  const handleBulkCreate = (bulkData) => {
    console.log('Creating bulk shifts:', bulkData);
    // In real app, this would create multiple shifts
    alert(`${bulkData?.departments?.length * bulkData?.selectedDays?.length} Schichten werden erstellt!`);
  };

  const handleEditShift = (shift) => {
    setSelectedShift(shift);
    setPreviewData(shift);
  };

  const getDepartmentLabel = (department) => {
    const departments = {
      'customer-service': 'Kundenservice',
      'operations': 'Betrieb',
      'manufacturing': 'Fertigung',
      'administration': 'Verwaltung',
      'security': 'Sicherheit'
    };
    return departments?.[department] || department;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success bg-success/10';
      case 'draft':
        return 'text-warning bg-warning/10';
      case 'cancelled':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Aktiv';
      case 'draft':
        return 'Entwurf';
      case 'cancelled':
        return 'Storniert';
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Schichtverwaltung</h1>
              <p className="text-muted-foreground mt-2">
                Erstellen, bearbeiten und verwalten Sie Arbeitsschichten für Ihr Team
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                onClick={() => setShowTemplateLibrary(true)}
                iconName="BookOpen"
                iconPosition="left"
                iconSize={16}
              >
                Vorlagen
              </Button>
              
              <Button
                variant="outline"
                onClick={() => setShowBulkModal(true)}
                iconName="Copy"
                iconPosition="left"
                iconSize={16}
              >
                Massenerstelling
              </Button>
              
              <Button
                onClick={() => {
                  setSelectedShift(null);
                  setPreviewData(null);
                }}
                iconName="Plus"
                iconPosition="left"
                iconSize={16}
              >
                Neue Schicht
              </Button>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
            {/* Form Section */}
            <div className="xl:col-span-3">
              <ShiftForm
                selectedShift={selectedShift}
                onSave={handleSaveShift}
                onCancel={handleCancelEdit}
                onPreview={handlePreviewUpdate}
              />
            </div>

            {/* Preview Section */}
            <div className="xl:col-span-2">
              <PreviewPanel
                previewData={previewData}
                conflicts={[]}
                suggestions={[]}
              />
            </div>
          </div>

          {/* Recent Shifts */}
          {recentShifts?.length > 0 && (
            <div className="mt-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">Kürzlich erstellte Schichten</h2>
                <Button
                  variant="ghost"
                  iconName="ArrowRight"
                  iconPosition="right"
                  iconSize={16}
                >
                  Alle anzeigen
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recentShifts?.slice(0, 3)?.map((shift) => (
                  <div
                    key={shift?.id}
                    className="bg-card border border-border rounded-lg p-6 hover:shadow-card transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-foreground">{shift?.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {getDepartmentLabel(shift?.department)}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-md ${getStatusColor(shift?.status)}`}>
                        {getStatusLabel(shift?.status)}
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Icon name="Calendar" size={14} className="mr-2" />
                        {new Date(shift.date)?.toLocaleDateString('de-DE')}
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Icon name="Clock" size={14} className="mr-2" />
                        {shift?.startTime} - {shift?.endTime}
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Icon name="Users" size={14} className="mr-2" />
                        {shift?.assignedEmployees}/{shift?.maxEmployees} Mitarbeiter
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        Erstellt: {new Date(shift.createdAt)?.toLocaleDateString('de-DE')}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditShift(shift)}
                        iconName="Edit"
                        iconPosition="left"
                        iconSize={14}
                      >
                        Bearbeiten
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Calendar" size={24} className="text-primary" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-foreground">24</p>
                  <p className="text-sm text-muted-foreground">Schichten diese Woche</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={24} className="text-success" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-foreground">89%</p>
                  <p className="text-sm text-muted-foreground">Besetzungsrate</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="AlertTriangle" size={24} className="text-warning" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-foreground">3</p>
                  <p className="text-sm text-muted-foreground">Offene Schichten</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="BookOpen" size={24} className="text-accent" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-foreground">12</p>
                  <p className="text-sm text-muted-foreground">Aktive Vorlagen</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Modals */}
      {showTemplateLibrary && (
        <TemplateLibrary
          onSelectTemplate={handleSelectTemplate}
          onClose={() => setShowTemplateLibrary(false)}
        />
      )}
      {showBulkModal && (
        <BulkCreationModal
          onClose={() => setShowBulkModal(false)}
          onCreateBulk={handleBulkCreate}
        />
      )}
    </div>
  );
};

export default ShiftCreationAndEditing;